import { TKeyDate, TaskPreferenceItem, FileTaskItem, ApplicableDate } from "./taskTypes";
import unobservedHolidays from '../unobservedHolidays.json';

export function mapKeydateToID(
    provinceKeyDates: TKeyDate[], 
    allTasks: TaskPreferenceItem[]
): TaskPreferenceItem[] {
    const updatedTasks = [...allTasks];
    updatedTasks.forEach((task: TaskPreferenceItem) => {
        if (task.CalendarItemDate) {
            let matchingProvinceTask = provinceKeyDates.find(
                provinceTask => provinceTask.title === task.CalendarItemDate
            );
            if(!matchingProvinceTask && task.CalendarItemDate === 'Requisition'){
                matchingProvinceTask = provinceKeyDates.find(
                    provinceTask => provinceTask.title === 'Objection'
                );
            }
            if(!matchingProvinceTask && task.CalendarItemDate === 'Closing'){
                matchingProvinceTask = provinceKeyDates.find(
                    provinceTask => provinceTask.title === 'Possession'
                );
            }
            if(!matchingProvinceTask && task.CalendarItemDate === 'Conditional'){
                matchingProvinceTask = provinceKeyDates.find(
                    provinceTask => provinceTask.title === 'Conditional Until'
                );
            }
            if(!matchingProvinceTask && task.CalendarItemDate === 'Interim Closing'){
                matchingProvinceTask = provinceKeyDates.find(
                    provinceTask => provinceTask.title === 'Interim Possession'
                );
            }
            if (matchingProvinceTask) {
                task.CalendarItemID = matchingProvinceTask.id;
            }
        }
    });
    return updatedTasks;
}

export function modifyTaskDate(
    task: FileTaskItem, 
    matchingTaskProfile: TaskPreferenceItem, 
    matchingKeyDate: FileTaskItem | ApplicableDate, 
    isOverwriteDate: number,
    province: string,
    ignoreNonBusinessDays: boolean,
    hdObj: any
): FileTaskItem {
    if(isOverwriteDate !== 1){
        const newDate = matchingKeyDate.date;
        const offset = matchingTaskProfile.offset;
        const beforeAfter = matchingTaskProfile.beforeAfter;
        const keyDateObj = new Date(`${newDate} 10:00:00`); // 10h offset from utc0
        const currentYear = new Date().getFullYear();
        const years = [currentYear - 1, currentYear, currentYear + 1];
        const offsetMs = parseInt(offset) * 24 * 60 * 60 * 1000;
        const allHolidays = getAllHolidaysForYears(hdObj, province, years);
        if(!ignoreNonBusinessDays){
            task.date = getDate(offsetMs, beforeAfter === 'before' ? -1 : 1, keyDateObj);
        } else {
            task.date = getBusinessDate(province, beforeAfter, parseInt(offset), allHolidays, task, keyDateObj);
        }       
    }
    return task;
}

export function modifyTaskPerson(
    task: FileTaskItem, 
    matchingTaskProfile: TaskPreferenceItem, 
    matchingKeyDate: FileTaskItem | ApplicableDate, 
    overwritePerson: number, 
    firmContact: string[], 
    lawyerOnFile: string, 
    constants: Record<string, string | number>
): FileTaskItem {
    if(overwritePerson !== 1){
        let userResponsible: string[] = [];
        const idsAssignedTo: string = matchingTaskProfile.idsAssignedTo;
        if(idsAssignedTo > ""){
            const responsibleUsers: string[] = idsAssignedTo.split(",");
            responsibleUsers.forEach((user: string) => {
                const userParts: string[] = user.split("-");
                if(userParts && userParts[1]){
                    const subUserID: string = userParts[1];
                    switch(subUserID) {
                        case `${constants.ID_FIRMCONTACT}`:
                            if(firmContact[1] && firmContact[1] !== 'undefined' && firmContact[0] !== 'staff') {
                                userResponsible.push(`${firmContact[1]}`);
                            }
                            break;
                        case `${constants.ID_LAWYERONFILE}`:
                            if(lawyerOnFile && lawyerOnFile !== 'undefined') {
                                userResponsible.push(lawyerOnFile);
                            }
                            break;
                        default:
                            userResponsible.push(subUserID);
                            break;
                    }
                }
            })
        }
        task.userResponsible = userResponsible.length > 0 ? userResponsible.join(",") : undefined;
    }
    return task;
}

export function setDescriptions(
    inFileTask: FileTaskItem, 
    taskSettings: Record<string, string>, 
    taskProfile: TaskPreferenceItem, 
    psm: string, 
    clientName: string, 
    clientNameSurnames: string, 
    matterNumber: string, 
    lawyerInitials: string, 
    clerkInitials: string, 
    reLine: string
): FileTaskItem {
    let shortDescr: string = "";
    let longDescr: string = "";
    let strShort: string = "";
    let l_initials: string = "";
    let initials: string = "";
    let startPreference: string = "";

    if(taskSettings.ShowTransactionTypeShort === '1'){
        switch(psm){
            case "Purchase":
            case "P":
                startPreference = "P: ";
                break;
            case "Sale":
            case "S":
                startPreference = "S: ";
                break;
            case "Mortgage":
            case "M":
                startPreference = "M: ";
                break;
        }
    } else if(taskSettings.ShowTransactionType === '1'){
        switch(psm){
            case "Purchase":
            case "P":
                startPreference = "Purchase: ";
                break;
            case "Sale":
            case "S":
                startPreference = "Sale: ";
                break;
            case "Mortgage":
            case "M":
                startPreference = "Mortgage: ";
                break;
        }
    }

    if(taskSettings.ShowClientName === '1'){
        if(clientNameSurnames > ""){
            startPreference += clientNameSurnames;
        } else {
            startPreference += clientName ?? "";
        }
    }

    if(taskSettings.ShowFileNumber === '1'){
        if(taskSettings.ShowClientName === '1'){
            startPreference += " ";
        }
        startPreference += matterNumber;
    }

    if(lawyerInitials && taskSettings.ShowLawyer === '1'){
        initials = lawyerInitials;
        initials = initials.toUpperCase();
    }

    if(clerkInitials && taskSettings.ShowClerk === '1'){
        l_initials = clerkInitials;
        l_initials = l_initials.toLowerCase();
        if(l_initials > ""){
            if(initials > ""){
                initials = initials + "/" + l_initials;
            } else {
                initials = l_initials;
            }
        }
    }

    if(initials > ""){
        startPreference = startPreference + " (" + initials + ")";
    }

    strShort = " (" + taskProfile.description + ")";
    shortDescr = "" + startPreference + strShort;

    switch(psm){
        case "Purchase":
        case "P":
            longDescr = "Purchase File: " + reLine + strShort;
            break;
        case "Sale":
        case "S":
            longDescr = "Sale File: " + reLine + strShort;
            break;
        case "Mortgage":
        case "M":
            longDescr = "Mortgage File: " + reLine + strShort;
            break;
    }
    inFileTask.title = shortDescr;
    inFileTask.matterNumber = longDescr;
    return inFileTask;
}

//TODO
export function modifyTaskNotes(
    task: FileTaskItem, 
    matchingTaskProfile: TaskPreferenceItem,
    matchingKeyDate: FileTaskItem | ApplicableDate, 
    overwriteDate: number, 
    overwritePerson: number
): FileTaskItem {
    return task;
}

export function modifyTaskActivated(
    task: FileTaskItem, 
    matchingTaskProfile: TaskPreferenceItem
): FileTaskItem {
    task.isActivated = matchingTaskProfile.output === '1' ? 1 : 0;
    return task;
}

export function checkTaskPSM(
    task: FileTaskItem, 
    matchingTaskProfile: TaskPreferenceItem, 
    psm: string
): FileTaskItem {
    if(!psm || psm === ""){
        task.isActivated = 0;
    }
    switch(psm){
        case "Purchase":
        case "P":
            if(!matchingTaskProfile.applyToFileType.includes("P")){
                task.isOverwriteActivate = 1;
                task.isActivated = 0;
            }
            else{
                task.details = "P";
                task.isOverwriteActivate = 0;
            }
            break;
        case "Sale":
        case "S":
            if(!matchingTaskProfile.applyToFileType.includes("S")){
                task.isOverwriteActivate = 1;
                task.isActivated = 0;
            }
            else{
                task.details = "S";
                task.isOverwriteActivate = 0;
            }
            break;
        case "Mortgage":
        case "M":
            if(!matchingTaskProfile.applyToFileType.includes("M")){
                task.isOverwriteActivate = 1;
                task.isActivated = 0;
            }
            else{
                task.details = "M";
                task.isOverwriteActivate = 0;
            }
            break;
    }
    return task;
}

export function addNewTask(
    typeID: string,
    recordID: string,
    ownerID: string,
    userResponsible?: string,
    overwriteDate?: number,
    isCompleted?: number,
    notes?: string, 
    overwritePerson?: number
): FileTaskItem {
    const newTask = {
        typeID: parseInt(typeID),
        appId: recordID,
        userResponsible: userResponsible,
        overwriteDate: overwriteDate,
        isCompleted: isCompleted,
        notes: notes,
        overwritePerson: overwritePerson,
        date: new Date().toISOString().split('T')[0],
        ownerID: ownerID,
        title: '',
        matterNumber: ''
    }
    return newTask;
}

export function modifyExistingTask(
    inFileTask: FileTaskItem,
    provinceTasks: TKeyDate[],
    taskProfile: TaskPreferenceItem,
    keyDate: FileTaskItem | ApplicableDate,
    firmContact: string[], 
    lawyerOnFile: string,
    constants: Record<string, string | number>,
    psm: string,
    province: string,
    hdObj: any,
    ignoreNonBusinessDays: boolean
): FileTaskItem {
    const overwriteDate: number = inFileTask.overwriteDate;
    const overwritePerson: number = inFileTask.overwritePerson;
    const isCompleted: number = inFileTask.isCompleted;
    if(isCompleted === 0 || !isCompleted){
        if (!taskProfile.CalendarItemID || !(taskProfile.CalendarItemID > "")) {
            taskProfile = mapKeydateToID(provinceTasks, [taskProfile])[0]
        }
        inFileTask = modifyTaskDate(inFileTask, taskProfile, keyDate, overwriteDate, province, ignoreNonBusinessDays, hdObj);
        inFileTask = modifyTaskPerson(inFileTask, taskProfile, keyDate, overwritePerson, firmContact, lawyerOnFile, constants);
        inFileTask = modifyTaskNotes(inFileTask, taskProfile, keyDate, overwriteDate, overwritePerson);
        inFileTask = modifyTaskActivated(inFileTask, taskProfile);
        inFileTask = checkTaskPSM(inFileTask, taskProfile, psm);
    }
    return inFileTask;
}

export function removeExistingTask(
    inFileTask: FileTaskItem
): FileTaskItem {
    inFileTask.isOverwriteActivate = 1;
    inFileTask.isActivated = 0;
    return inFileTask;
}

export function processTaskProfile(
    taskProfile: TaskPreferenceItem,
    province: string,
    myDates: ApplicableDate[],
    inFileTasks: FileTaskItem[],
    provinceTasks: TKeyDate[],
    firmContact: string[],
    lawyerOnFile: string,
    appID: string,
    ownerID: string,
    taskSettings: Record<string, string>,
    psm: string,
    clientName: string,
    clientNameSurnames: string,
    matterNumber: string,
    lawyerInitials: string,
    clerkInitials: string,
    reLine: string,
    constants: Record<string, string | number>,
    hdObj: any,
    ignoreNonBusinessDays: boolean = true,
    isMassUpdate?: boolean
): Record<string, FileTaskItem | boolean> {

    let createNewTask: boolean = false;
    let updatedTask: FileTaskItem = null;
    let keyDate: FileTaskItem | ApplicableDate;
    const CalendarItemID = taskProfile.CalendarItemID as string;
    
    if(isMassUpdate){
        keyDate = inFileTasks.find(inFileTask => parseInt(inFileTask.xcondition) === parseInt(CalendarItemID)) as FileTaskItem;
    } else {
        keyDate = myDates.find(myDate => myDate.id === parseInt(CalendarItemID)) as ApplicableDate;
    }

    if(CalendarItemID) {
        let inFileTask: FileTaskItem = inFileTasks.find(task => task.typeID === parseInt(taskProfile.typeID));
        const isKeyDateDefined: boolean = keyDate && keyDate.date && keyDate.date > ""; 
        if(inFileTask) {
            if(isKeyDateDefined || inFileTask?.isCompleted === 1){
                updatedTask = modifyExistingTask(inFileTask, provinceTasks, taskProfile, keyDate, firmContact, lawyerOnFile, constants, psm, province, hdObj, ignoreNonBusinessDays);
            } else {
                updatedTask = removeExistingTask(inFileTask)
            }
        } else if(isKeyDateDefined) {
            createNewTask = true;
            updatedTask = addNewTask(taskProfile.typeID, appID, ownerID);
            updatedTask = modifyExistingTask(updatedTask, provinceTasks, taskProfile, keyDate, firmContact, lawyerOnFile, constants, psm, province, hdObj, ignoreNonBusinessDays);
        }
        if(updatedTask){
            updatedTask = setDescriptions(updatedTask, taskSettings, taskProfile, psm, clientName, clientNameSurnames, matterNumber, lawyerInitials, clerkInitials, reLine)
            return {updatedTask, createNewTask};
        }
    }
    return {
        updatedTask: null, 
        createNewTask: false
    };
}

export function getAllHolidaysForYears(hdObj: any, prov: string, years: number[]): any[] {
    let allHolidays: any[] = [];
    years.forEach(year => {
        allHolidays = allHolidays.concat(hdObj.getHolidays(year));
    });
    return allHolidays;
}

function getDate(offsetMs: number, direction: number, keyDateObj: Date): string {
    keyDateObj.setTime(keyDateObj.getTime() + (direction * offsetMs));
    const year = keyDateObj.getFullYear();
    const month = String(keyDateObj.getMonth() + 1).padStart(2, '0');
    const day = String(keyDateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function getObservedHolidays(
    prov: string,
    allHolidays: any[]
): { [date: string]: string } {
    const notObserved = unobservedHolidays[prov] || [];
    const cleanString = (str: string) => str.replace(/[\s,.'']/g, '').toLowerCase();
    const observed = allHolidays.filter(
        h => h.type === 'public' && !notObserved.some(name => cleanString(name) === cleanString(h.name))
    );
    const map: { [date: string]: string } = {};
    observed.forEach(h => {
        if (h.date) map[h.date.substring(0, 10)] = h.name;
    });
    return map;
}

function isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6;
}

function isHoliday(date: Date, holidaysMap: { [date: string]: string }): boolean {
    const ymd = date.toISOString().substring(0, 10);
    return !!holidaysMap[ymd];
}

export function getBusinessDate(
    prov: string,
    beforeAfter: string,
    offset: number,
    allHolidays: any[],
    taskItem?: FileTaskItem,
    refDate?: Date
): string {
    if (beforeAfter === 'on' || offset === 0) {
        return getDate(offset, 0, refDate);
    }

    const holidaysMap = getObservedHolidays(prov, allHolidays);
    const direction = beforeAfter === 'before' ? -1 : 1;
    const absOffset = Math.abs(offset);
    let count = 0;

    while (count < absOffset) {
        refDate.setDate(refDate.getDate() + direction);
        if (!isWeekend(refDate) && !isHoliday(refDate, holidaysMap)) {
            count++;
        }
    }
    return getDate(0, 0, refDate);
}
